import React, {memo} from 'react';

import cs from 'classnames';
import filter from 'lodash/filter';
import {Link} from 'react-router';
import styled from 'styled-components';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import {ItemRelationsConnection} from 'app/folders/components/ItemRelationsConnection';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {ItemRelationsBadges} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsListOfTeams} from 'app/general/dashboards/ItemRelationsListOfTeams';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {
  DocumentPrivateStatusIcon,
  DocumentSequencedStepsStatusIcon,
} from 'app/general/doc-status';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {standardizeRawProcedure} from 'app/procedures/utils';
import {DocNeedsReviewBadge} from 'app/review/components/DocNeedReviewBadge';
import {getProcedureName} from 'app/utils/fallbacks';
import {deepDiffer} from 'app/utils/react-utils';

import type {BadgeRelation} from 'app/general/dashboards/ItemRelationsBadges';
import type {NotificationType} from 'app/notifly/types';
import type {
  ConnectionFieldSerializedType,
  ContentType,
  ProcedureSerializedType,
  RawProcedureSerializedType,
} from 'sweet-types';

type CompactProps = {
  refreshData: () => Promise<unknown>;
  object: ProcedureSerializedType;
  extra?: {
    ordering?: string;
    activeRelations?: Record<ContentType, boolean>;
    showRelations?: boolean;
    toggleProceduresDashboardRelation?: (
      type: ContentType,
      activeState: boolean
    ) => void;
  };
  proceduresNotifications?: Record<string, Array<NotificationType>>;
};

const StyledItemDiv = styled.div`
  position: relative;
`;

const relations: Array<BadgeRelation> = [
  {
    ct: 'tag',
    getter: (p: RawProcedureSerializedType) => p.tags,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'team',
    getter: (p: RawProcedureSerializedType) =>
      Array.from(new Set(p.team_memberships.map((team) => team.team))),
    Component: ItemRelationsListOfTeams,
  },
  {
    ct: 'proceduregroup',
    getter: (p: RawProcedureSerializedType) => p.processes || [],
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'decision',
    getter: (p: RawProcedureSerializedType) => {
      const procedure = standardizeRawProcedure(p);
      return procedure.content
        .filter((content) => content.type === 'decision')
        .map((content) => ({
          ...content,
          name: content.title,
        }));
    },
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'policy',
    getter: (p: ProcedureSerializedType) => p.policies || [],
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'folder',
    getter: (p: ProcedureSerializedType) => {
      const connections = p.connections;
      return filter(
        connections,
        (connection: ConnectionFieldSerializedType) => {
          return (
            connection.team == null &&
            connection.process == null &&
            connection.folder != null
          );
        }
      );
    },
    Component: ItemRelationsConnection,
  },
];

export const CompactProcedureListItem = memo(
  ({
    object,
    refreshData,
    extra: {
      ordering,
      showRelations,
      activeRelations,
      toggleProceduresDashboardRelation,
    } = {},
    proceduresNotifications,
  }: CompactProps) => (
    <StyledItemDiv
      className={cs('row border-bottom py-2 d-flex align-items-center', {
        'opacity-overlay': object.approved_at == null,
      })}
    >
      <DashboardItemPresentMembers
        contentType="procedure"
        objectId={object.id}
      />

      <StyledCompactItemHeaderDiv>
        <Link
          to={object.html_url}
          className="text-dark font-weight-bold d-block d-sm-inline"
        >
          <StyledCompactItemHeaderSpan>
            <DocumentIconWithNotifications
              contentType="procedure"
              notifications={(proceduresNotifications || {})[object.url]}
            />{' '}
            {getProcedureName(object, true)}
            <DocIsDraftBadge approved_at={object.approved_at} />
            {object.next_review_at != null ? (
              <DocNeedsReviewBadge reviewDate={object.next_review_at} />
            ) : null}
          </StyledCompactItemHeaderSpan>
        </Link>
      </StyledCompactItemHeaderDiv>

      {showRelations === true && toggleProceduresDashboardRelation != null ? (
        <ItemRelationsBadges
          className="col-auto ml-auto ml-sm-0 order-lg-3"
          relations={relations}
          object={object}
          activeRelations={activeRelations}
          onToggle={toggleProceduresDashboardRelation}
        />
      ) : null}

      <div className="col-10 col-sm-6 col-lg-3 mr-auto order-lg-4">
        <ItemLatestActivity object={object} sortBy={ordering} />
      </div>

      {object.steps_blocked_at != null && (
        <div className="pl-0 col-2 col-sm-auto text-right col-lg-auto order-lg-2">
          <DocumentSequencedStepsStatusIcon
            isSequenced={object.steps_blocked_at != null}
            contentType="procedure"
          />
        </div>
      )}

      {object.private !== undefined && !object.private ? (
        <div className="pl-0 col-2 col-sm-auto text-right col-lg-auto order-lg-2">
          <DocumentPrivateStatusIcon isPrivate={false} ct="procedure" />
        </div>
      ) : null}

      <div className="col-auto order-lg-5">
        <DocumentActionsButton
          document={object}
          pageContext="dashboard"
          refreshData={refreshData}
        />
      </div>

      {showRelations === true &&
      Object.keys(activeRelations || {}).length > 0 ? (
        <div className="col-12 order-last">
          <ItemRelationsLists
            object={object}
            activeRelations={activeRelations}
            relations={relations}
          />
        </div>
      ) : null}
    </StyledItemDiv>
  ),
  (props, nextProps) => !deepDiffer(props, nextProps)
);
